import * as React from "react";
import { useMemo } from "react";
import { WithEditModeViewerProps } from "../../sharedHOC/withEditMode/withEditMode";
import { ColoredSelectedItem } from "../colorCoding/ColoredSelectedItem";
import { ColoredNamedItem, getItemValue, getItemLabel as labelSelector, ItemKey } from "../list";
import { InlinePopupSelectVirtProps } from "../select";
import { Marker } from "../userflow/utils/marker";

type Props<TOption, TValue> = Marker &
    Partial<
        Pick<InlinePopupSelectVirtProps<TOption, TValue>, "value" | "getItemLabel" | "options" | "valueSelector">
    > & {
        initialText?: string;
    };

type InlineEditSelectViewer<TOption, TValue> = React.FunctionComponent<
    WithEditModeViewerProps & Props<TOption, TValue>
>;

function useSelectedOption<TOption, TValue>(props: Props<TOption, TValue>) {
    const { options, value, getItemLabel, valueSelector, initialText } = props;

    const option = useMemo(() => {
        if (!value) {
            return null;
        }
        return (options || []).find(x => valueSelector(x) === value);
    }, [options, value]);
    const text = option ? getItemLabel(option) : value && initialText;

    return { option, text };
}

export const InlineEditSelectViewer: InlineEditSelectViewer<any, any> = props => {
    const {
        value,
        wrapperClassName,
        options,
        initialText,
        getItemLabel = labelSelector,
        valueSelector = getItemValue,
        "data-testid": testId,
    } = props;

    const { text } = useSelectedOption({ value, options, getItemLabel, valueSelector, initialText });

    return (
        <div className={wrapperClassName} data-testid={testId}>
            {text}
        </div>
    );
};

export const InlineEditSelectColoredViewer: InlineEditSelectViewer<ColoredNamedItem, ItemKey> = props => {
    const { option, text } = useSelectedOption(props);

    return (
        <div className={props.wrapperClassName} data-testid="colored-chips">
            {!!option && <ColoredSelectedItem label={text} color={option.color} />}
        </div>
    );
};
